import { useEffect, useRef, useState, useCallback } from 'react';
import jsQR from 'jsqr';


// ─── QRScanner ────────────────────────────────────────────────────────────────
// Reusable camera-based QR code scanner.
// Props:
//   onScan(tokenId)  — called when a valid Raider Depot token QR is decoded
//   onError(msg)     — called on camera permission denial or decode error
//   onClose()        — called when user dismisses the scanner
//   active           — boolean, controls whether camera is running
//
// The QR codes generated by this platform encode the token URL:
//   https://riverwoodraiderdepot.org/order?token=RD-RW-001
// This component extracts the token parameter from that URL.
// If the QR content is just a plain token string (RD-RW-NNN), that also works.

export default function QRScanner({ onScan, onError, onClose, active = true }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | scanning | found | error
  const [errorMsg, setErrorMsg] = useState('');
  const [lastResult, setLastResult] = useState('');

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const extractToken = useCallback((text) => {
    // Try URL format: ?token=RD-RW-001
    try {
      const url = new URL(text);
      const token = url.searchParams.get('token');
      if (token) return token.toUpperCase().trim();
    } catch (_) {}
    // Try plain token format: RD-RW-001
    const match = text.match(/RD-[A-Z]{2}-\d{3,}/i);
    if (match) return match[0].toUpperCase().trim();
    return null;
  }, []);

  const scan = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });
    if (code) {
      const token = extractToken(code.data);
      if (token && token !== lastResult) {
        setLastResult(token);
        setStatus('found');
        stopCamera();
        onScan(token);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(scan);
  }, [extractToken, lastResult, onScan, stopCamera]);

  useEffect(() => {
    if (!active) { stopCamera(); return; }

    let cancelled = false;
    setStatus('starting');
    setErrorMsg('');

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: 'environment', // rear camera on phones
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setStatus('scanning');
          rafRef.current = requestAnimationFrame(scan);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow camera access and try again.'
          : 'Could not access camera. Please check your device settings.';
        setStatus('error');
        setErrorMsg(msg);
        if (onError) onError(msg);
      });

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [active, scan, stopCamera, onError]);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: 400,
      margin: '0 auto',
      background: '#000',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Video feed */}
      <video
        ref={videoRef}
        muted
        playsInline
        style={{
          width: '100%',
          display: status === 'scanning' ? 'block' : 'none',
        }}
      />

      {/* Hidden canvas for frame analysis */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Scanning overlay */}
      {status === 'scanning' && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          {/* Corner brackets */}
          {[
            { top: '20%', left: '15%', borderTop: `3px solid #FFF`, borderLeft: `3px solid #FFF` },
            { top: '20%', right: '15%', borderTop: `3px solid #FFF`, borderRight: `3px solid #FFF` },
            { bottom: '20%', left: '15%', borderBottom: `3px solid #FFF`, borderLeft: `3px solid #FFF` },
            { bottom: '20%', right: '15%', borderBottom: `3px solid #FFF`, borderRight: `3px solid #FFF` },
          ].map((s, i) => (
            <div key={i} style={{
              position: 'absolute', width: 28, height: 28, ...s
            }} />
          ))}
          <div style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            position: 'absolute',
            bottom: '12%',
            textAlign: 'center',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          }}>
            Point camera at QR code
          </div>
        </div>
      )}

      {/* Starting state */}
      {status === 'starting' && (
        <div style={{
          padding: '48px 24px', textAlign: 'center', background: '#111',
        }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
            Starting camera…
          </div>
        </div>
      )}

      {/* Found state */}
      {status === 'found' && (
        <div style={{
          padding: '48px 24px', textAlign: 'center', background: '#0F2A1A',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 14, color: '#4ADE80', fontWeight: 600 }}>
            Token scanned: {lastResult}
          </div>
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div style={{
          padding: '32px 20px', textAlign: 'center', background: '#1A0A0A',
        }}>
          <div style={{ fontSize: 13, color: '#FCA5A5', marginBottom: 16, lineHeight: 1.5 }}>
            {errorMsg}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.3)',
              color: '#FFF', padding: '8px 20px', borderRadius: 8,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Enter token manually
          </button>
        </div>
      )}

      {/* Close button */}
      {status === 'scanning' && (
        <button
          onClick={() => { stopCamera(); onClose(); }}
          style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(0,0,0,0.5)', border: 'none',
            color: '#FFF', width: 32, height: 32, borderRadius: '50%',
            fontSize: 18, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
